import React, { useState } from 'react';
import { ArrowClockwise, Download } from 'react-bootstrap-icons';
import { useError } from "../contexts/ErrorContext";

interface DownloadComponentProps {
  name: string;
  urls: string[];
}

const DownloadComponent: React.FC<DownloadComponentProps> = ({ name, urls }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { setError } = useError();

  const downloadFile = async (url: string): Promise<void> => {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to download from ${url}: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Extract filename from URL without query parameters
      let filename = 'download';
      const urlWithoutParams = url.split('?')[0];
      if (urlWithoutParams) {
        filename = urlWithoutParams.substring(urlWithoutParams.lastIndexOf('/') + 1);
      }

      // Create a temporary anchor element to trigger the download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(`Error downloading ${url}:`, error);
      throw error;
    }
  };

  const handleDownloadAll = async () => {
    if (urls.length === 0) {
      console.warn('No URLs provided for download');
      return;
    }

    setIsDownloading(true);
    setProgress(0);

    let successCount = 0;
    let failureCount = 0;
    let completedCount = 0;
    const CONCURRENCY_LIMIT = 2; // Download 2 files simultaneously (safer for browser limits)

    try {
      // Process downloads with controlled concurrency
      for (let i = 0; i < urls.length; i += CONCURRENCY_LIMIT) {
        // Get the next batch of URLs (up to CONCURRENCY_LIMIT)
        const batch = urls.slice(i, i + CONCURRENCY_LIMIT);

        // Download all files in this batch concurrently
        const batchPromises = batch.map(async (url, batchIndex) => {
          const overallIndex = i + batchIndex;
          try {
            await downloadFile(url);
            successCount++;
          } catch (error) {
            console.error(`Failed to download ${url}:`, error);
            setError(`Failed to download file ${overallIndex + 1}/${urls.length}: ${error}`);
            failureCount++;
            // Continue with other downloads even if one fails
          } finally {
            completedCount++;
            // Update progress after each file
            setProgress(Math.round((completedCount / urls.length) * 100));
          }
        });

        // Wait for all downloads in this batch to complete
        await Promise.all(batchPromises);

        // Add a small delay between batches to avoid overwhelming the browser
        // Skip delay after the last batch
        if (i + CONCURRENCY_LIMIT < urls.length) {
          await new Promise(resolve => setTimeout(resolve, 400));
        }
      }

      // Log summary
      console.log(`Download complete: ${successCount} successful, ${failureCount} failed out of ${urls.length} total files`);

      if (failureCount > 0) {
        setError(`Download completed with ${failureCount} failed file(s) out of ${urls.length} total`);
      }

    } catch (error) {
      console.error('Error during downloads:', error);
      setError(`Error during downloads: ${error}`);
    } finally {
      setIsDownloading(false);
      setProgress(100);
    }
  };

  return (
    <div className="download-component">
      <button
        className="btn btn-primary d-flex align-items-center gap-1"
        onClick={handleDownloadAll}
        disabled={isDownloading || urls.length === 0}
      >
        {isDownloading ? (
          <>
            <ArrowClockwise className="spin-animation" size={16} />
            Downloading {name} ({progress}%) (in nb of files)
          </>
        ) : (
          <>
            <Download size={16} />
            Download {name}
          </>
        )}
      </button>

      {isDownloading && (
        <div className="progress mt-2">
          <div
            className="progress-bar"
            role="progressbar"
            style={{ width: `${progress}%` }}
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {progress}%
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadComponent;
